import { FC, ReactNode, useContext, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Grid, Link, Stack, Typography } from '@mui/material';

import clsx from 'clsx';
import _uniq from 'lodash/uniq';

import { AnalyticsContext, EVENTS, getHistoricalReturnRange, useIdentify, useTrack } from '@arrived/analytics';
import { useIsLoggedIn } from '@arrived/auth';
import { calculatorAppreciationToEquity, useStrOfferingData } from '@arrived/common';
import { useGetPropertyStatus } from '@arrived/contexts';
import { AirBnbLogo } from '@arrived/images';
import {
  AccountId,
  AccountOfferingBalance,
  AssetType,
  getLeaseStatusMessageDisplayText,
  MarketROI,
  Offering,
  OfferingStatus,
  Property,
  SecurityType,
} from '@arrived/models';
import { useGetAccountOfferingBalancesQuery, useMarketROIQuery, useOfferingDividendsQuery } from '@arrived/queries';

import { OfferingName } from '../../OfferingName';
import { PropertyDetailsOfferingBalance } from '../../Portfolio';
import { PropertyThumbnailProps } from '../../Property';
import { AccreditedOfferingBadge } from '../AccreditedOfferingBadge';
import { PROPERTY_HASH_MAP } from '../Property.constants';
import { CommonQuestions } from './CommonQuestions';
import { OtherQuestions } from './CommonQuestions/OtherQuestions';
import { WhichProperty } from './CommonQuestions/WhichProperty';
import { StyledPropertyDetails } from './PropertyDetails.styles';
import { PropertyDetailsCarousel } from './PropertyDetailsCarousel';
import { PropertyDetailsCTA } from './PropertyDetailsCTA';
import { PropertyDetailsDescription } from './PropertyDetailsDescription';
import { PropertyDetailsHistoricalReturns } from './PropertyDetailsHistoricalReturns';
import { PropertyDetailsManagementInfo } from './PropertyDetailsManagementInfo';
import { PropertyDetailsMarketInfo } from './PropertyDetailsMarketInfo';
import { PropertyDetailsOfferingDetails } from './PropertyDetailsOfferingDetails';
import { PropertyDetailsPageHeader } from './PropertyDetailsPageHeader';
import { PropertyDetailsRentInfo } from './PropertyDetailsRentInfo';
import { PropertyDetailsZillowTrend } from './PropertyDetailsZillowTrend';

import { useIsMobile, useMediaQueryClasses } from '../../hooks';
import { LTR_COMMON_QUESTIONS, STR_COMMON_QUESTIONS } from './config/commonQuestionsConfig';
import { PropertyDetailsFinancialsSection } from './financials';
import { PropertyDetailsHeader } from './header';
import { LimitedOfferingDisclaimer } from './limitedOffering';
import { PropertyDetailsQuickLook } from './quickLook';
import { LeverageAndVolatility } from './volatility';

export interface PropertyDetailsProps {
  accountId?: AccountId;
  offering: Offering;
  marketData?: MarketROI;
  propertyActionButton: ReactNode;
  footer: ReactNode;
  topOffset?: number;
  handleOnBackButtonClick: () => void;
  limitWidth?: boolean;
  PropertyThumbnailComponent: FC<PropertyThumbnailProps>;
}

export const PropertyDetails = ({
  accountId,
  offering,
  marketData,
  propertyActionButton,
  footer,
  topOffset,
  handleOnBackButtonClick,
  limitWidth,
  PropertyThumbnailComponent,
}: PropertyDetailsProps) => {
  const {
    assetType,
    id: offeringId,
    debtPercent,
    isFund,
    name,
    properties,
    market,
    maxSharesPerAccount,
    securityType,
    sharePrice,
  } = offering;

  const property = properties[0] || ({} as Property);
  const { address, photos } = property;
  const isLTR = offering.assetType === AssetType.LTR;
  const identify = useIdentify();
  const { rudder } = useContext(AnalyticsContext);

  const strOfferingData = useStrOfferingData(offering.id);

  const isLoggedIn = useIsLoggedIn();
  const track = useTrack();

  const isMobile = useIsMobile();
  const mediaQueryClasses = useMediaQueryClasses();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const getPropertyStatus = useGetPropertyStatus();
  const marketROIState = useMarketROIQuery(market?.id);
  const offeringDividendsState = useOfferingDividendsQuery(offeringId);
  const accountOfferingBalancesState = useGetAccountOfferingBalancesQuery(accountId);

  useEffect(() => {
    const userTraits = rudder?.getUserTraits();
    identify({
      viewed_offering_details_ids: _uniq(
        [offering.id].concat((userTraits?.['viewed_offering_details_ids'] ?? []) as number[])
      ),
    });
  }, []);

  const offeringBalance = useMemo(
    () =>
      accountOfferingBalancesState.data?.offerings.find(
        (checkOffering: AccountOfferingBalance) => checkOffering.id === offering.id
      ),
    [accountOfferingBalancesState.data, offering.id]
  );

  const FooterComponent = () => (
    <>
      {/* Ensures a 2rem gap before the footer content, regardless of margin collapsing:
        https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Box_Model/Mastering_margin_collapsing */}
      <br />
      <br />
      {footer}
    </>
  );

  useEffect(() => {
    if (marketROIState.isSuccess && offeringDividendsState.isSuccess) {
      const property = offering.properties[0];
      const marketROI = marketROIState.data;
      const offeringDividends = offeringDividendsState.data;
      track(EVENTS.PROPERTY_VIEWED, {
        asset_type: offering.assetType,
        property_status: getPropertyStatus(offering).toLowerCase(),
        beds: property?.bedrooms,
        baths: (property?.fullBathrooms ?? 0) + (property?.halfBathrooms ?? 0),
        sq_ft: property?.squareFootage,
        year_built: property?.yearBuilt,
        number_of_investors: offering.investorsCount,
        property_purchase_price: offering.totalPurchasePrice,
        monthly_rent: property?.rent,
        offering_id: offeringId,
        offering_name: offering.name.toLowerCase(),
        perc_purchased_status: offering.fundedPercent,
        property_rental_status: getLeaseStatusMessageDisplayText(property.leaseStatus),
        first_dividend_date: offering.projectedFirstDividendDate ?? undefined,
        first_dividend_yield: offering.projectedAnnualDividendYield ?? undefined,
        ...getHistoricalReturnRange(
          offering,
          {
            lower: calculatorAppreciationToEquity(offering.projectedAppreciationLower ?? 0, offering),
            upper: calculatorAppreciationToEquity(offering.projectedAppreciationUpper ?? 0, offering),
          },
          offeringDividends
        ),
        historical_rental_dividends: marketROI.averageDividend,
        property_value_growth: offering.projectedAppreciationLower ?? undefined,
      });
    }
  }, [offeringId, marketROIState.status, offeringDividendsState.status]);

  return (
    <>
      <StyledPropertyDetails
        limitWidth={limitWidth}
        className={clsx('property-details-wrapper', mediaQueryClasses, {
          'market-offering': isFund,
        })}
      >
        <PropertyDetailsPageHeader offering={offering} handleOnBackButtonClick={handleOnBackButtonClick} />
        <PropertyDetailsCarousel
          offering={offering}
          propertyName={name}
          photos={photos || []}
          PropertyThumbnailComponent={PropertyThumbnailComponent}
        />
        <Grid container spacing={2} pt={{ md: 3 }}>
          <Grid item xs={12} md={8} lg={8}>
            <PropertyDetailsHeader offering={offering} />
            {!isMobile && securityType === SecurityType.REG_D && <AccreditedOfferingBadge sx={{ pt: 2, pl: 4 }} />}
            {/* TODO: it would be nice to not have to hide this when the Offering has been funded, but we have to for
                  now because these numbers appear weird when share prices have been changed. Maybe instead we describe
                  that it's a limited share count, not a price cap.
            */}
            {maxSharesPerAccount != null && offering.status !== OfferingStatus.FUNDED && (
              <LimitedOfferingDisclaimer
                maxSharesPerAccount={maxSharesPerAccount}
                offeringName={name}
                sharePrice={sharePrice}
              />
            )}
            {isLoggedIn && offeringBalance != null && (
              <>
                <PropertyDetailsOfferingBalance offeringBalance={offeringBalance} offering={offering} />
                <Typography
                  className="about-the-offering"
                  variant="h5.semibold"
                  color={({ palette }) => palette.darkened.steel}
                >
                  <FormattedMessage
                    id="about-the-offering"
                    defaultMessage="About {offeringName}"
                    values={{ offeringName: <OfferingName name={name} /> }}
                  />
                </Typography>
              </>
            )}

            <Grid container px={3} spacing={2} mt={{ xs: 1, md: 0 }}>
              <Grid item xs={12} md={assetType === AssetType.STR && strOfferingData.airBnbLink ? 6 : 12}>
                <PropertyDetailsQuickLook offering={offering} />
              </Grid>
              {assetType === AssetType.STR && strOfferingData.airBnbLink && (
                <Grid
                  item
                  xs={12}
                  md={6}
                  display="flex"
                  alignItems="flex-end"
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  <Stack direction="row" gap={0.5}>
                    <AirBnbLogo />
                    <Link
                      href={strOfferingData.airBnbLink}
                      typography={{ variant: 'body1' }}
                      sx={{ textDecoration: 'underline' }}
                    >
                      <FormattedMessage id="view-air-bnb-listing" defaultMessage="View airbnb listing" />
                    </Link>
                  </Stack>
                </Grid>
              )}
            </Grid>
            <PropertyDetailsDescription offering={offering} />

            {isLTR && <PropertyDetailsRentInfo offering={offering} />}
            {!isLTR && <PropertyDetailsManagementInfo offering={offering} />}

            <PropertyDetailsHistoricalReturns
              marketData={marketData}
              debtLeverage={debtPercent / 100}
              offering={offering}
              address={address}
            />
            <LeverageAndVolatility debtPercent={offering.debtPercent} offering={offering} />
            <PropertyDetailsMarketInfo market={market} property={property} offering={offering} />

            {isLTR && <PropertyDetailsZillowTrend address={address} market={market} />}

            <PropertyDetailsOfferingDetails offering={offering} />

            {isLTR && <PropertyDetailsFinancialsSection offering={offering} />}
            {isLTR && (
              <CommonQuestions id={PROPERTY_HASH_MAP.commonQuestions} commonQuestionsKeys={LTR_COMMON_QUESTIONS} />
            )}
            {assetType === AssetType.STR && (
              <CommonQuestions id={PROPERTY_HASH_MAP.commonQuestions} commonQuestionsKeys={STR_COMMON_QUESTIONS} />
            )}

            <WhichProperty />
            <OtherQuestions />

            {isMobile && <FooterComponent />}
          </Grid>

          <PropertyDetailsCTA offering={offering} propertyActionButton={propertyActionButton} topOffset={topOffset} />
        </Grid>
      </StyledPropertyDetails>

      {!isMobile && <FooterComponent />}
    </>
  );
};
